import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Users, Building2, Activity, Settings, Plus, Edit2, Trash2, RotateCcw } from 'lucide-react'
import { supabase, isDemoMode } from '@/lib/supabase'
import { DEMO_USER, DEMO_FAMILIES } from '@/lib/demoData'
import { RoleBadge } from '@/components/shared/StatusBadge'
import { ROLES, ACTIVITY_FAMILY_NAMES } from '@/lib/constants'
import type { UserProfile, Company } from '@/types'

// =============================================================================
// Admin page tabs
// =============================================================================

type AdminTab = 'users' | 'companies' | 'catalog' | 'settings'

const TABS = [
  { key: 'users' as AdminTab,     label: 'Usuarios',     icon: Users },
  { key: 'companies' as AdminTab, label: 'Empresas',     icon: Building2 },
  { key: 'catalog' as AdminTab,   label: 'Catálogo',     icon: Activity },
  { key: 'settings' as AdminTab,  label: 'Configuración',icon: Settings },
]

// =============================================================================
// Users Tab
// =============================================================================

function UsersTab() {
  const queryClient = useQueryClient()

  const { data: users, isLoading } = useQuery<UserProfile[]>({
    queryKey: ['admin-users'],
    queryFn: async () => {
      if (isDemoMode) return [
        DEMO_USER,
        { ...DEMO_USER, id: 'demo-02', role: 'SUPERVISOR_COBRA' as const, full_name: 'Carlos García López',   phone: '+34 600 111 222', company: { ...DEMO_USER.company!, name: 'Cobra Energía' } },
        { ...DEMO_USER, id: 'demo-03', role: 'SUBCONTRATISTA' as const,   full_name: 'Mecano Trackers SL',    phone: '+34 600 333 444', company: { ...DEMO_USER.company!, id: 'sub-01', name: 'Mecano Trackers SL' } },
        { ...DEMO_USER, id: 'demo-04', role: 'SUBCONTRATISTA' as const,   full_name: 'Electra Obras SA',      phone: '+34 600 555 666', company: { ...DEMO_USER.company!, id: 'sub-02', name: 'Electra Obras SA' } },
        { ...DEMO_USER, id: 'demo-05', role: 'CONSULTA_CLIENTE' as const, full_name: 'Navabuena Solar S.L.',  phone: '+34 600 777 888', company: { ...DEMO_USER.company!, id: 'cli-01', name: 'Navabuena Solar S.L.' } },
      ] as UserProfile[]
      const { data, error } = await supabase
        .from('user_profiles')
        .select(`*, company:companies(name)`)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as UserProfile[]
    },
  })

  const toggleActive = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { error } = await supabase
        .from('user_profiles')
        .update({ is_active: active })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-600">{users?.length ?? 0} usuarios registrados</p>
        <button className="btn-primary text-sm">
          <Plus className="w-4 h-4" />
          Invitar usuario
        </button>
      </div>

      <div className="card overflow-hidden">
        <table className="table-base">
          <thead>
            <tr>
              <th>Usuario</th>
              <th>Email</th>
              <th>Rol</th>
              <th>Empresa</th>
              <th>Estado</th>
              <th>Último acceso</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {(users ?? []).map((u) => (
              <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-xs font-bold shrink-0">
                      {u.full_name?.slice(0, 2).toUpperCase() ?? 'U'}
                    </div>
                    <span className="text-xs font-medium text-gray-800">
                      {u.full_name ?? '—'}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-gray-600">{u.id.slice(0, 8)}...</td>
                <td className="px-4 py-3"><RoleBadge role={u.role} /></td>
                <td className="px-4 py-3 text-xs text-gray-600">
                  {(u.company as { name: string } | null)?.name ?? '—'}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    u.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {u.is_active ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-gray-400">
                  {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString('es-ES') : '—'}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button className="p-1 rounded text-gray-400 hover:text-brand-600 hover:bg-brand-50">
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => toggleActive.mutate({ id: u.id, active: !u.is_active })}
                      className="p-1 rounded text-gray-400 hover:text-orange-600 hover:bg-orange-50"
                      title={u.is_active ? 'Desactivar' : 'Activar'}
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// =============================================================================
// Companies Tab
// =============================================================================

function CompaniesTab() {
  const queryClient = useQueryClient()

  const { data: companies, isLoading } = useQuery<Company[]>({
    queryKey: ['admin-companies'],
    queryFn: async () => {
      if (isDemoMode) return [
        { id: 'cobra-energy', name: 'Cobra Energía', cif: 'A-12345678', contact_name: 'Antonio Muñoz', contact_email: 'antonio.munoz@cobra.com', contact_phone: '+34 91 000 0000', is_active: true, created_at: '2026-01-01T00:00:00Z', updated_at: '2026-01-01T00:00:00Z', deleted_at: null },
        { id: 'sub-01', name: 'Mecano Trackers SL', cif: 'B-87654321', contact_name: 'Javier Ruiz', contact_email: 'jruiz@mecano.es', contact_phone: '+34 91 111 2222', is_active: true, created_at: '2026-01-01T00:00:00Z', updated_at: '2026-01-01T00:00:00Z', deleted_at: null },
        { id: 'sub-02', name: 'Electra Obras SA', cif: 'A-11223344', contact_name: 'María Torres', contact_email: 'm.torres@electra.es', contact_phone: '+34 91 333 4444', is_active: true, created_at: '2026-01-01T00:00:00Z', updated_at: '2026-01-01T00:00:00Z', deleted_at: null },
        { id: 'sub-03', name: 'Civil Navabuena UTE', cif: null, contact_name: 'Raúl Sánchez', contact_email: 'rsanchez@civilnte.es', contact_phone: '+34 91 555 6666', is_active: true, created_at: '2026-01-01T00:00:00Z', updated_at: '2026-01-01T00:00:00Z', deleted_at: null },
        { id: 'cli-01', name: 'Navabuena Solar S.L.', cif: 'B-99887766', contact_name: 'Laura Gómez', contact_email: 'lgomez@navabuena.es', contact_phone: '+34 91 777 8888', is_active: true, created_at: '2026-01-01T00:00:00Z', updated_at: '2026-01-01T00:00:00Z', deleted_at: null },
      ] as Company[]
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .is('deleted_at', null)
        .order('name')
      if (error) throw error
      return (data ?? []) as Company[]
    },
  })

  if (isLoading) {
    return <div className="flex items-center justify-center py-16">
      <div className="w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
    </div>
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-600">{companies?.length ?? 0} empresas registradas</p>
        <button className="btn-primary text-sm">
          <Plus className="w-4 h-4" />
          Nueva empresa
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {(companies ?? []).map((c) => (
          <div key={c.id} className="card p-4">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{c.name}</p>
                {c.cif && <p className="text-xs text-gray-400 mt-0.5">CIF: {c.cif}</p>}
              </div>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ml-2 ${
                c.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
              }`}>
                {c.is_active ? 'Activa' : 'Inactiva'}
              </span>
            </div>
            {c.contact_name && (
              <p className="text-xs text-gray-600 mt-3">
                Contacto: <span className="font-medium">{c.contact_name}</span>
              </p>
            )}
            {c.contact_email && (
              <p className="text-xs text-gray-500 mt-0.5">{c.contact_email}</p>
            )}
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
              <button className="btn-ghost text-xs py-1">
                <Edit2 className="w-3 h-3" />
                Editar
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// =============================================================================
// Catalog Tab
// =============================================================================

function CatalogTab() {
  const { data: families, isLoading } = useQuery({
    queryKey: ['admin-catalog'],
    queryFn: async () => {
      if (isDemoMode) return DEMO_FAMILIES
      const { data, error } = await supabase
        .from('activity_families')
        .select(`*, templates:activity_templates(count)`)
        .order('display_order')
      if (error) throw error
      return data ?? []
    },
  })

  if (isLoading) {
    return <div className="flex items-center justify-center py-16">
      <div className="w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
    </div>
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600 mb-4">{families?.length ?? 0} familias de actividad configuradas</p>
      {(families ?? []).map((f) => (
        <div key={f.id} className="card px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm font-bold text-brand-700 w-8">{f.code}.</span>
              <div>
                <p className="text-sm font-semibold text-gray-900">{f.name}</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {f.discipline.replace(/_/g, ' ')} ·{' '}
                  {f.per_parcel ? '26 parcelas' :
                   f.special_parcels ? `Parcelas ${f.special_parcels.join(', ')}` : 'Global'}
                </p>
              </div>
            </div>
            <span className="text-xs text-gray-500">
              {(f.templates as Array<{ count: number }>)?.[0]?.count ?? 0} subactividades
            </span>
          </div>
        </div>
      ))}
    </div>
  )
}

// =============================================================================
// Settings Tab
// =============================================================================

function SettingsTab() {
  return (
    <div className="space-y-4 max-w-lg">
      <div className="card p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Configuración general</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
              Nombre del proyecto
            </label>
            <input type="text" defaultValue="PSFV Navabuena" className="input-base text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
              Potencia instalada (MWp)
            </label>
            <input type="number" placeholder="e.g. 300" className="input-base text-sm" />
          </div>
        </div>
        <button className="btn-primary text-sm mt-4">Guardar cambios</button>
      </div>

      <div className="card p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Integridad de datos</h3>
        <p className="text-xs text-gray-500 mb-4">
          Ejecuta validaciones de integridad sobre los pesos de las familias de actividad.
        </p>
        <button className="btn-secondary text-sm">
          <Activity className="w-4 h-4" />
          Validar pesos de familias
        </button>
      </div>
    </div>
  )
}

// =============================================================================
// AdminPage
// =============================================================================

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState<AdminTab>('users')

  const content = {
    users:     <UsersTab />,
    companies: <CompaniesTab />,
    catalog:   <CatalogTab />,
    settings:  <SettingsTab />,
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-gray-900">Administración</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Gestión de usuarios, empresas y configuración del sistema
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200">
        {TABS.map((tab) => {
          const Icon     = tab.icon
          const isActive = activeTab === tab.key
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`
                flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors
                ${isActive
                  ? 'border-brand-600 text-brand-700 bg-brand-50'
                  : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }
              `}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          )
        })}
      </div>

      {/* Tab content */}
      <div>{content[activeTab]}</div>
    </div>
  )
}
